import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 */
class ProductPrice extends HTMLElement {
  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  /**
   * Updates the price with smooth animation.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const newPrice = event.detail.data.html.querySelector('product-price [ref="priceContainer"]');
    const currentPrice = this.querySelector('[ref="priceContainer"]');

    if (!newPrice || !currentPrice) return;

    if (currentPrice.innerHTML !== newPrice.innerHTML) {
      this.animatePrice(currentPrice, newPrice);
    }
  };

  /**
   * Animate price change with smooth transition
   * @param {HTMLElement} currentPrice - Current price element
   * @param {HTMLElement} newPrice - New price element
   */
  animatePrice(currentPrice, newPrice) {
    // Add animation class
    currentPrice.classList.add('price-updating');
    
    // Replace content IMMEDIATELY but animate the transition
    setTimeout(() => {
      currentPrice.innerHTML = newPrice.innerHTML;
    }, 150); // Content switch at peak blur
    
    // Create smooth transition
    const keyframes = [
      { 
        transform: 'scale(1) translateY(0px)', 
        opacity: 1,
        filter: 'blur(0px)'
      },
      { 
        transform: 'scale(1.05) translateY(-2px)', 
        opacity: 0.6,
        filter: 'blur(2px)'
      },
      { 
        transform: 'scale(1) translateY(0px)', 
        opacity: 1,
        filter: 'blur(0px)'
      }
    ];

    const options = {
      duration: 300,
      easing: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
      fill: 'forwards'
    };

    // Start animation immediately
    const animation = currentPrice.animate(keyframes, options);
    
    animation.onfinish = () => {
      currentPrice.classList.remove('price-updating');
    };
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
